// Copy of components/transactionSearchPage/DateFilterPill.js wired to the Payments - Beta date picker.
import React, { Fragment, useEffect, useRef } from "react";
import styled from "styled-components";
import moment from "moment-timezone";
import { noop } from "@/util/common";
import DatePicker from "@/components/datePickerV2Beta";
import { Chip } from "@/components/transactionSearchPage/FilterPill";
import { isDateRangePopulated } from "@/components/transactionSearchPage/filterPresentation";

const DISPLAY_DATE_FORMAT = "MM/DD/YYYY";

const toDisplayDate = (date) =>
  date ? moment(date).format(DISPLAY_DATE_FORMAT) : "";

// Widest figure in Public Sans at the pill's size; re-measure if the face
// changes.
const WIDEST_DIGIT = "8";

const WIDTH_RESERVATION = `${DISPLAY_DATE_FORMAT.replace(
  /[MDY]/g,
  WIDEST_DIGIT
)} – ${DISPLAY_DATE_FORMAT.replace(/[MDY]/g, WIDEST_DIGIT)}`;

/**
 * HiddenCalendarMount
 *
 * Keeps `DatePicker` mounted with its inline date fields hidden, so the pill
 * can open its calendar modal (portaled to `document.body`). Consequence: no
 * typed date entry on this page.
 */
const HiddenCalendarMount = styled.div`
  display: none;
`;

/**
 * DateFilterPillV2Beta
 *
 * The search row's date filter. It has no panel: clicking opens the calendar
 * modal, which applies on Enter and resets from its own link, so the pill has
 * no Apply or Clear buttons.
 *
 * @param {object} selectedDateFilter - Transaction date or funded date.
 * @param {Function} selectDateFilter - Switches between those two.
 * @param {object} searchStartDate - The range's start, a moment or null.
 * @param {object} searchEndDate - The range's end, a moment or null.
 * @param {Function} onApply - Runs the search. The calendar calls it on Enter.
 * @param {Function} onReset - Restores the default range and re-runs the
 * search with it.
 * @param {boolean} disabled - Freezes the pill while a search runs.
 */
const DateFilterPillV2Beta = ({
  label = "Date",
  dataQa,
  icon,
  selectedDateFilter,
  selectDateFilter,
  searchStartDate,
  searchEndDate,
  onApply = noop,
  onReset = noop,
  openCalendar = noop,
  size = "default",
  disabled = false,
}) => {
  // Enter closes the calendar, and focus comes back here rather than to the
  // date fields this pill hides (WCAG 2.4.3).
  const pillButtonRef = useRef(null);
  // Enter also starts a search, which disables this pill before the calendar
  // returns focus, and a disabled button refuses focus. So the focus waits
  // for the search to end, unless the user has moved on by then.
  const focusOwedRef = useRef(false);
  const returnFocusRef = useRef({
    focus: () => {
      const button = pillButtonRef.current;
      if (button?.disabled) {
        focusOwedRef.current = true;
      } else {
        button?.focus();
      }
    },
  });

  useEffect(() => {
    if (disabled || !focusOwedRef.current) return;
    focusOwedRef.current = false;
    const active = document.activeElement;
    if (!active || active === document.body) pillButtonRef.current?.focus();
  }, [disabled]);

  const hasRange = !!(searchStartDate && searchEndDate);
  const displayValue = hasRange
    ? `${toDisplayDate(searchStartDate)} – ${toDisplayDate(searchEndDate)}`
    : null;

  return (
    <Fragment>
      <Chip
        label={label}
        displayValue={displayValue}
        populated={isDateRangePopulated(searchStartDate, searchEndDate)}
        isOpen={false}
        onToggle={disabled ? noop : openCalendar}
        onClose={noop}
        dataQa={dataQa}
        icon={icon}
        size={size}
        widthReservation={WIDTH_RESERVATION}
        // No chevron or aria-expanded: nothing expands. `aria-haspopup` points at
        // the calendar modal.
        chevron={false}
        hasPanel={false}
        buttonRef={pillButtonRef}
        disabled={disabled}
      />
      <HiddenCalendarMount>
        <DatePicker
          selectedDateFilter={selectedDateFilter}
          selectDateFilter={selectDateFilter}
          closeOnEnter
          onApply={onApply}
          onResetRange={onReset}
          returnFocusRef={returnFocusRef}
        />
      </HiddenCalendarMount>
    </Fragment>
  );
};

export default DateFilterPillV2Beta;
